"use client";

import { forwardRef } from "react";

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  highlightMissing?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, highlightMissing, id, className = "", type, ...rest }, ref) => {
    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, "-");
    return (
      <div className="flex flex-col gap-1">
        {label && (
          <label
            htmlFor={inputId}
            className={[
              "text-sm font-medium transition-colors",
              highlightMissing ? "text-danger" : "text-muted",
            ].join(" ")}
          >
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          type={type}
          className={[
            "w-full rounded-md px-3 py-2 text-sm border transition-colors",
            "text-foreground input-placeholder",
            "enabled:bg-input disabled:bg-background disabled:cursor-not-allowed",
            "read-only:bg-background read-only:cursor-default",
            error || highlightMissing
              ? "border-danger focus:border-danger"
              : "border-input-border focus:border-accent",
            highlightMissing ? "field-missing-blink" : "",
            "focus:outline-none focus:ring-1 focus:ring-accent",
            "disabled:focus:ring-0 disabled:focus:border-input-border",
            "read-only:focus:ring-0 read-only:focus:border-input-border",
            type === "number" ? "number-input-dark" : "",
            className,
          ].join(" ")}
          {...rest}
        />
        {error && <p className="text-xs text-danger">{error}</p>}
        {hint && !error && <p className="form-hint">{hint}</p>}
      </div>
    );
  }
);
Input.displayName = "Input";

// ── Textarea variant ─────────────────────────────────────────────────────────

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, hint, id, className = "", ...rest }, ref) => {
    const textareaId = id ?? label?.toLowerCase().replace(/\s+/g, "-");
    return (
      <div className="flex flex-col gap-1">
        {label && (
          <label htmlFor={textareaId} className="text-sm text-muted font-medium">
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={textareaId}
          className={[
            "w-full rounded-md px-3 py-2 text-sm border transition-colors",
            "text-foreground input-placeholder resize-y min-h-[80px]",
            "enabled:bg-input disabled:bg-background disabled:cursor-not-allowed",
            "read-only:bg-background read-only:cursor-default",
            error
              ? "border-danger focus:border-danger"
              : "border-input-border focus:border-accent",
            "focus:outline-none focus:ring-1 focus:ring-accent",
            "disabled:focus:ring-0 disabled:focus:border-input-border",
            "read-only:focus:ring-0 read-only:focus:border-input-border",
            className,
          ].join(" ")}
          {...rest}
        />
        {error && <p className="text-xs text-danger">{error}</p>}
        {hint && !error && <p className="form-hint">{hint}</p>}
      </div>
    );
  }
);
Textarea.displayName = "Textarea";
